import _ from 'lodash';

import request from '../api/request';
import {Sorting, Paging, EquipmentFieldTypes} from '../helpers/constants';
import {TicketListType} from '../reducers/ticketReducer';
import {genericLoad, genericPut} from '../helpers/reduxHelpers';
import {
  objectToParams,
  handleError,
  flattenSortOrder,
  formValidation,
} from '../helpers';
import {ticketActionTypes as actions} from './actionTypes';
import {asyncLoad, asyncPost} from '.';

export const loadSpecificTickets = ticketIds => (dispatch, getState) =>
  asyncLoad({
    dispatch,
    title: getState().portal.dictionary.Ticket.pluralValue,
    url: `/tickets/bulk?ticketIds=${ticketIds.join('&ticketIds=')}`,
  });

export const getTicketEmailList = ticketId => dispatch =>
  asyncLoad({
    dispatch,
    title: 'email list',
    url: `/tickets/${ticketId}/email-list`,
  });

export const getTicketPhoneNumbers = ticketId => dispatch =>
  asyncLoad({
    dispatch,
    title: 'Phone Number',
    url: `/tickets/${ticketId}/phone-numbers`,
  });

export const updateTicketListType = (listType = TicketListType.allTickets) => ({
  type: actions.UPDATE_TICKET_LIST_TYPE,
  payload: listType,
});

export const updatePaging = ({pageNumber, pageSize}) => ({
  type: actions.TICKETS_LIST.PAGE_CHANGED,
  payload: {
    pageNumber,
    pageSize,
  },
});

export const changeSort = ({sortColumn, sortOrder}) => ({
  type: actions.TICKETS_LIST.SORT_CHANGED,
  payload: {
    sortColumn,
    sortOrder,
  },
});

export const checkAll = isChecked => ({
  type: actions.TICKETS_LIST.ALL_CHECKED_CHANGED,
  payload: isChecked,
});

export const checkItem = ticket => ({
  type: actions.TICKETS_LIST.ITEM_CHECKED_CHANGED,
  payload: ticket,
});

export const changeFilterOption = filterOption => ({
  type: actions.TICKETS_LIST.FILTER_OPTION_CHANGED,
  payload: filterOption,
});

export const loadTicket = ticketId => (dispatch, getState) => {
  const {portal} = getState();
  const ticketKeyword = portal.dictionary.Ticket.pluralValue;
  return asyncLoad({
    dispatch,
    title: ticketKeyword,
    url: `/tickets/${ticketId}`,
  });
};

export const updateTicket = (ticket, patch = false) => async (
  dispatch,
  getState,
) => {
  const {portal} = getState();
  const ticketKeyword = portal.dictionary.Ticket.value;
  const formObject = formValidation.setupFormBody(ticket, patch);

  if (formObject.hasError) {
    handleError(formObject.errorMessage, null, dispatch);
    return null;
  }

  const contactPatch = formObject.body.find(x => x.path === '/contactId');

  if (Number(formObject.body.contactId) === 0) {
    formObject.body.contactId = null;
  } else if (contactPatch && Number(contactPatch.value) === 0) {
    contactPatch.value = null;
  }

  try {
    return asyncPost({
      body: formObject.body,
      dispatch,
      params: {method: patch ? 'PATCH' : 'PUT'},
      successMessage: `Successfully saved`,
      title: ticketKeyword,
      url: `/tickets/${ticket.ticketId.value}`,
    });
  } catch (e) {
    return null;
  }
};

export const loadTickets = ({
  filterOption = 0,
  filterParams = {},
  groupBy = '',
  pageInfo = {},
  sortInfo = {},
  listType = '',
  startDate = '',
  endDate = '',
  signal,
}) => (dispatch, getState) => {
  const {sortColumn, sortOrder} = sortInfo;
  const {pageNumber} = pageInfo;
  const pageSize =
    pageInfo.pageSize === 0 ? Paging.initialPageSize : pageInfo.pageSize;
  const {portal, tickets} = getState();
  const body = {
    ...filterParams,
    customFilterId: filterOption,
    groupBy: groupBy === null ? '' : groupBy,
    isAssignedToMe: tickets.listType === TicketListType.myTickets,
    isDashboard: tickets.listType === TicketListType.allTickets && !listType,
    limit: pageSize,
    orderBy: sortColumn,
    orderDir: flattenSortOrder(sortOrder),
    start: (pageNumber - 1) * pageSize,
    listType,
    startDate,
    endDate,
  };
  const params = objectToParams(body);
  const ticketKeyword = portal.dictionary.Ticket.pluralValue;

  dispatch(changeFilterOption(Number(filterOption)));
  dispatch(
    changeSort({
      sortColumn,
      sortOrder: sortOrder === 'desc' ? Sorting.desc : Sorting.asc,
    }),
  );
  dispatch(updatePaging({pageNumber, pageSize}));
  genericLoad({
    dispatch,
    field: 'tickets',
    payloadModifier: data => {
      if (groupBy === 'Status') {
        const groups = _.groupBy(data.data, 'TicketStatus');
        const tickets = [];

        Object.keys(groups).forEach(group => {
          tickets.push({
            TicketId: Math.random()
              .toString(32)
              .substr(2, 8),
            header: group,
          });
          tickets.push(...groups[group]);
        });

        return {data: tickets, total: data.total};
      }

      return data;
    },
    title: ticketKeyword.toLowerCase(),
    type: actions.TICKETS,
    url: `/tickets/${
      listType || startDate
        ? 'customlist'
        : tickets.listType === TicketListType.allTickets
          ? 'admindashboard'
          : 'dashboard'
    }?${params}`,
    useMAPI: true,
    signal,
  });
};

export const loadTicketStats = (onlyMyTickets = false, signal) => (dispatch) => {
  genericLoad({
    dispatch,
    field: 'ticketStats',
    stateField: 'stats',
    title: 'ticket stats',
    type: actions.TICKET_STATS,
    url: `/tickets/getstats?onlyMyTicket=${onlyMyTickets}`,
    useMAPI: true,
    signal,
  });
};

export const loadTicketColumns = () => dispatch =>
  genericLoad({
    dispatch,
    field: 'ticketColumns',
    stateField: 'tableColumns',
    title: 'ticket columns',
    type: actions.TICKET_COLUMNS,
    url: '/ticketsettings',
    useMAPI: true,
  });

export const updateTicketColumns = body => dispatch =>
  genericPut({
    body,
    dispatch,
    field: 'updateTicketColumns',
    successMessage: 'The table columns have been updated.',
    title: 'table columns',
    type: actions.UPDATE_TICKET_COLUMNS,
    url: '/ticketsettings/batch',
    useMAPI: true,
  });

export const isElasticSearchEnabled = () => dispatch => {
  genericLoad({
    dispatch,
    field: 'elasticSearchEnabled',
    stateField: 'elasticSearchEnabled',
    title: 'Elastic Search',
    type: actions.ELASTIC_SEARCH,
    url: '/ticket/iselasticsearchenabled',
    useMAPI: true,
  });
};

export const loadPriorityColors = () => dispatch =>
  genericLoad({
    dispatch,
    field: 'priorityColors',
    stateField: 'priorityColors',
    title: 'priority colors',
    type: actions.PRIORITY_COLORS,
    url: '/ticketpriorities/colors',
    useMAPI: true,
  });

export const loadTicketColorsAsync = () => (dispatch, getState) =>
  asyncLoad({
    dispatch,
    title: `${getState().portal.dictionary.Ticket.value} colors`,
    url: '/tickets/colors',
  });

export const loadTicketTypeColors = () => dispatch =>
  genericLoad({
    dispatch,
    field: 'ticketTypeColors',
    stateField: 'ticketTypeColors',
    title: 'ticket type colors',
    type: actions.TICKET_TYPE_COLORS,
    url: '/tickettypes/colors',
    useMAPI: true,
  });

export const loadTicketStatusesMAPI = () => dispatch =>
  genericLoad({
    dispatch,
    field: 'ticketStatuses',
    stateField: 'ticketStatuses',
    title: 'ticket statuses',
    type: actions.TICKET_STATUSES,
    url: '/ticketstatuses',
    useMAPI: true,
  });

export const loadTicketStatuses = () => dispatch =>
  asyncLoad({
    dispatch,
    title: 'Statuses',
    url: '/TicketStatus',
  });

export const changeTicketStatus = status => (dispatch, getState) =>
  genericPut({
    body: {
      ids: getState()
        .tickets.tickets.filter(
          // [Nhan Nguyen 2022-12-05]: [MHD-921] We need to filter ticket with TicketNumber,
          // because the ticket list might contain dummy tickets for rendering purposes on UI
          // and they were accidentally submitted to the back-end server
          ticket => ticket.TicketNumber && ticket.isChecked,
        )
        .map(ticket => ticket.TicketId),
      StatusId: status,
    },
    dispatch,
    field: 'ticketStatuses',
    includePortal: false,
    title: 'ticket statuses',
    type: actions.UPDATE_TICKET_STATUS,
    url: '/tickets/batch/status',
    useMAPI: true,
  });

export const printTickets = () => async (dispatch, getState) => {
  try {
    const response = await request({
      body: {
        ticketIds: getState()
          .tickets.tickets.filter(ticket => ticket.isChecked)
          .map(ticket => ticket.TicketId),
        isWorkOrder: false,
      },
      includePortal: false,
      method: 'POST',
      url: '/ticket/print',
      useMAPI: true,
    });

    return response.data.url;
  } catch (error) {
    handleError('Unable to update', error, dispatch);
    return null;
  }
};

export const updateCustomerLoadingValues = () => dispatch => {
  const customerLoadingExemptions = [
    {
      type: actions.PRIORITY_COLORS,
      field: 'priorityColors',
    },
    {
      type: actions.TICKET_TYPE_COLORS,
      field: 'ticketTypeColors',
    },
    {
      type: actions.TICKET_STATS,
      field: 'ticketStats',
    },
  ];

  customerLoadingExemptions.forEach(exemption =>
    dispatch({
      type: exemption.type.LOADING,
      field: exemption.field,
      loading: false,
    }),
  );
};

export const mapTicketColumns = (columns, dictionary) => {
  const mappedColumns = columns
    .filter(column => column.Value.toLowerCase() === 'true')
    .map(column => {
      let mapping = ticketColumnMappings[column.Name];
      let dictionaryValue = '';

      if (!mapping) {
        const headerText = column.Name
          ? column.Name.replace(/_/g, ' ')
          : 'Unknown';
        const displayType =
          column.ControlTypeId === EquipmentFieldTypes.Date ? 'dateOnly' : '';
        mapping = {dataField: column.Name, headerText, displayType};
      } else {
        dictionaryValue = dictionary[mapping.headerText];
      }

      return {
        ...mapping,
        ControlTypeId: column.ControlTypeId,
        headerTranslated: dictionaryValue
          ? dictionaryValue.value
          : mapping.headerText,
      };
    });

  // Swapping the Customer Column
  if (mappedColumns.length >= 4) {
    const index = mappedColumns.findIndex(x => x.dataField === 'CustomerName');
    let newColumn = 3;

    if (mappedColumns[newColumn].dataField === 'subject') {
      newColumn = 4;
    }

    if (index >= 5) {
      const customerColumn = mappedColumns[index];
      const swappingColumn = mappedColumns[newColumn];

      mappedColumns[newColumn] = customerColumn;
      mappedColumns[index] = swappingColumn;
    }
  }

  return mappedColumns;
};

const noWrapColumn = {
  whiteSpace: 'nowrap',
  width: '1%',
};

export const ticketColumnMappings = {
  ShowTicketID: {
    dataField: 'TicketId',
    headerText: 'ID',
    textAlign: 'center',
  },
  ShowPriority: {
    dataField: 'priority',
    headerText: '!',
    textAlign: 'center',
  },
  ShowSubject: {
    dataField: 'subject',
    headerText: 'TicketSubject',
    style: {
      minWidth: 300,
    },
  },
  ShowSubmitterId: {
    dataField: 'SubmitterUsername',
    headerText: 'ContactUsername',
    style: noWrapColumn,
  },
  ShowSubmitterName: {
    dataField: 'SubmitterFullname',
    headerText: 'CustomerContact',
    style: noWrapColumn,
  },
  ShowAssignedTo: {dataField: 'AssignedToName', headerText: 'AssignedTo'},
  ShowCloseBy: {
    dataField: 'neededBy',
    displayType: 'date',
    headerText: 'PreferredCompletionDate',
    style: noWrapColumn,
  },
  ShowCreationDate: {
    dataField: 'creationDate',
    displayType: 'date',
    headerText: 'Created',
    style: noWrapColumn,
  },
  ShowLastUpdated: {
    dataField: 'lastModDate',
    displayType: 'date',
    headerText: 'Updated',
    style: noWrapColumn,
  },
  ShowStatus: {dataField: 'TicketStatus', headerText: 'TicketStatus'},
  ShowScheduledDate: {
    dataField: 'AllAptDates',
    headerText: 'ScheduledDate',
    style: noWrapColumn,
  },
  ShowCopyButton: {
    dataField: 'CopyButton',
    headerText: 'Copy',
    displayType: 'icon',
    icon: 'zmdi zmdi-copy',
  },
  ShowCustomer: {
    dataField: 'CustomerName',
    headerText: 'CustomerName',
    style: noWrapColumn,
  },
  ShowBusinessUnitColumn: {
    dataField: 'BusinessUnit',
    headerText: 'Business Unit',
  },
  ShowServiceLocation: {
    dataField: 'SLFullAddress',
    headerText: 'ServiceLocation',
    style: {
      whiteSpace: 'nowrap',
      width: 300,
    },
  },
  ShowEstimateAmount: {
    dataField: 'EstimateAmount',
    headerText: 'Estimate Total',
    style: noWrapColumn,
  },
  ShowInvoiceAmount: {
    dataField: 'InvoiceAmount',
    headerText: 'Invoice Total',
    style: noWrapColumn,
  },
  ShowInvoiceBalance: {
    dataField: 'InvoiceBalance',
    headerText: 'Invoice Balance',
    style: noWrapColumn,
  },
  ShowInvoiceNumber: {
    dataField: 'InvoiceNumber',
    headerText: 'Invoice #',
    style: noWrapColumn,
  },
  ShowPONumber: {
    dataField: 'PONumber',
    headerText: 'PO #',
    style: noWrapColumn,
  },
  ShowTicketType: {
    dataField: 'TicketType',
    headerText: 'TicketType',
    style: noWrapColumn,
  },
};



// WEBPACK FOOTER //
// ./src/actions/ticketActions.js